import { useEffect, useState } from 'react';
import { Building2, Pencil, Plus, Trash2 } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import SearchInput from '../components/SearchInput';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { hospitalsAPI } from '../services/api';

const EMPTY_FORM = {
  name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  pincode: '',
  contact_phone: '',
  contact_email: '',
  network_type: 'cashless',
  is_active: true,
};

const NETWORK_TYPES = [
  { value: 'cashless', label: 'Cashless' },
  { value: 'reimbursement', label: 'Reimbursement' },
  { value: 'both', label: 'Cashless & Reimbursement' },
];

export default function HospitalManagement() {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const debouncedSearch = useDebouncedValue(search);

  useEffect(() => {
    let cancelled = false;

    const fetchHospitals = async () => {
      setSearching(true);
      try {
        const { data } = await hospitalsAPI.list({
          q: debouncedSearch || undefined,
          include_inactive: showInactive ? 'true' : undefined,
        });
        if (!cancelled) setHospitals(data);
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to load hospitals');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSearching(false);
        }
      }
    };

    fetchHospitals();
    return () => { cancelled = true; };
  }, [showInactive, debouncedSearch]);

  const loadHospitals = async () => {
    setSearching(true);
    try {
      const { data } = await hospitalsAPI.list({
        q: search || undefined,
        include_inactive: showInactive ? 'true' : undefined,
      });
      setHospitals(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load hospitals');
    } finally {
      setSearching(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowForm(true);
  };

  const openEdit = (hospital) => {
    setEditingId(hospital.id);
    setForm({
      name: hospital.name || '',
      address_line1: hospital.address_line1 || '',
      address_line2: hospital.address_line2 || '',
      city: hospital.city || '',
      state: hospital.state || '',
      pincode: hospital.pincode || '',
      contact_phone: hospital.contact_phone || '',
      contact_email: hospital.contact_email || '',
      network_type: hospital.network_type || 'cashless',
      is_active: hospital.is_active !== false,
    });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingId) {
        await hospitalsAPI.update(editingId, form);
      } else {
        await hospitalsAPI.create(form);
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      await loadHospitals();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save hospital');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (hospital) => {
    if (!confirm(`Delete hospital "${hospital.name}" (${hospital.city})?`)) return;
    setError('');
    try {
      await hospitalsAPI.remove(hospital.id);
      await loadHospitals();
    } catch (err) {
      setError(err.response?.data?.error || 'Cannot delete hospital');
    }
  };

  if (loading && hospitals.length === 0) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-7 h-7 text-primary-600" />
            Hospital Management
          </h1>
          <p className="text-slate-500 mt-1">Super Admin — maintain network hospital master data</p>
        </div>
        <button type="button" onClick={openCreate} className="btn-primary flex items-center gap-2 self-start">
          <Plus className="w-4 h-4" />
          Add Hospital
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="card flex flex-col sm:flex-row gap-4 sm:items-end">
        <SearchInput
          className="flex-1"
          label="Search"
          placeholder="Name, city, or state"
          value={search}
          onChange={setSearch}
        />
        <label className="flex items-center gap-2 text-sm text-slate-600 pb-2">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="rounded border-slate-300"
          />
          Show inactive
        </label>
        {searching && <span className="text-sm text-slate-500 pb-2">Updating…</span>}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-4">
          <h3 className="font-semibold text-lg">{editingId ? 'Update Hospital' : 'Add Hospital'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="label">Hospital Name *</label>
              <input className="input-field" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="label">City *</label>
              <input className="input-field" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div>
              <label className="label">State *</label>
              <input className="input-field" required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
            <div>
              <label className="label">Pincode *</label>
              <input className="input-field" required pattern="\d{6}" maxLength={6} value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="label">Address Line 1 *</label>
              <input className="input-field" required value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <label className="label">Address Line 2</label>
              <input className="input-field" value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} />
            </div>
            <div>
              <label className="label">Contact Phone</label>
              <input className="input-field" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
            </div>
            <div>
              <label className="label">Contact Email</label>
              <input type="email" className="input-field" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </div>
            <div>
              <label className="label">Network Type</label>
              <select className="input-field" value={form.network_type} onChange={(e) => setForm({ ...form, network_type: e.target.value })}>
                {NETWORK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {editingId && (
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-slate-700 pb-2">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  Active
                </label>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Update Hospital' : 'Add Hospital'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="pb-2 pr-4">Hospital</th>
              <th className="pb-2 pr-4">Location</th>
              <th className="pb-2 pr-4">Network</th>
              <th className="pb-2 pr-4">Contact</th>
              <th className="pb-2 pr-4">Status</th>
              <th className="pb-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {hospitals.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-8 text-center text-slate-500">No hospitals found</td>
              </tr>
            ) : hospitals.map((h) => (
              <tr key={h.id} className="border-b border-slate-100">
                <td className="py-3 pr-4 font-medium text-slate-900">{h.name}</td>
                <td className="py-3 pr-4 text-slate-600">
                  {h.city}, {h.state} — {h.pincode}
                  <div className="text-xs text-slate-400">{h.address_line1}</div>
                </td>
                <td className="py-3 pr-4 capitalize">{h.network_type?.replace('_', ' ')}</td>
                <td className="py-3 pr-4 text-slate-600">
                  {h.contact_phone || '—'}
                  {h.contact_email && <div className="text-xs">{h.contact_email}</div>}
                </td>
                <td className="py-3 pr-4">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${h.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600'}`}>
                    {h.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="py-3">
                  <div className="flex gap-2">
                    <button type="button" onClick={() => openEdit(h)} className="p-1.5 text-slate-500 hover:text-primary-600" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => handleDelete(h)} className="p-1.5 text-slate-500 hover:text-red-600" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
